import React, { useState } from "react";
import { ChevronRight, Loader2, Search } from "lucide-react";
import { OtcRequestItem } from "../../types";
import { Button } from "../../../../shared/ui";
import { formatCurrencyAmount } from "../../../transactions/utils/formatCurrency";
import { useOtcExternalAccounts } from "../../hooks/useOtcExternalAccounts";
import { useDebouncedValue } from "../../../../hooks/use-debounced-value";

import { CurrencyFlag } from "./CurrencyFlag";

export interface ProviderItem {
  id: string;
  name: string;
  accountNumber: string;
  companyName: string;
  avatarText?: string;
  bankCode?: string;
  bankCountry?: string;
  paymentMethod?: string;
  nameEnquiryReference?: string;
  relationship?: string;
  accountHolderType?: string;
  email?: string;
  routingNumber?: string;
  swiftCode?: string;
  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  addressCountry?: string;
  isDomestic?: boolean;
}

export interface ProviderSelectionStepProps {
  request: OtcRequestItem;
  selectedProviderId: string | null;
  savedProviders?: ProviderItem[];
  onSelectProvider: (provider: ProviderItem) => void;
  onAddNewProviderClick: () => void;
  onBack: () => void;
  onNext: () => void;
}

export const ProviderSelectionStep: React.FC<ProviderSelectionStepProps> = ({
  request,
  selectedProviderId,
  savedProviders,
  onSelectProvider,
  onAddNewProviderClick,
  onBack,
  onNext,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 350);

  // Currency directly from the passed OTC request state without fallback
  const currencyParam = request.currency;
  const sourceCurrency = request.currency.toUpperCase();
  const targetCurrency = request.currencyTo ? request.currencyTo.toUpperCase() : "";

  // Fetch payout accounts dynamically from endpoint:
  // GET /v1/admin/otc/external-accounts?currency=USD&search=...
  const {
    providers: apiProviders,
    isLoading,
    isError,
  } = useOtcExternalAccounts({
    currency: currencyParam,
  });

  const effectiveProviders = apiProviders.filter((prov) =>
    prov.name.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  const formattedAmount = formatCurrencyAmount(
    request.amount,
    request.currency
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Title */}
      <h2 className="text-[20px] font-semibold text-neutral-900">
        Enter provider details
      </h2>

      {/* Amount banner */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2.5 rounded-xl border border-neutral-200 bg-white px-4 py-3 shadow-xs">
          <div className="flex items-center -space-x-1">
            <CurrencyFlag currency={sourceCurrency} className="size-5" />
            <CurrencyFlag currency={targetCurrency} className="size-5" />
          </div>
          <span className="text-[14px] font-semibold text-neutral-900">
            Sending {formattedAmount}
          </span>
        </div>

        {/* Master wallet funded notice */}
        <div className="flex items-center gap-2 px-1 text-[12px] text-neutral-500">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
          <span>
            Our (Nuvion) master wallet must be sufficiently funded for this transfer
          </span>
        </div>
      </div>

      {/* Use new provider button card (commented out: users only see search bar and available providers) */}
      {/* <button
        type="button"
        onClick={onAddNewProviderClick}
        className="flex w-full items-center justify-between rounded-xl border border-neutral-200 bg-white p-3.5 shadow-xs transition-colors hover:border-neutral-300 hover:bg-neutral-50"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-badge text-brand">
            <User size={16} />
          </div>
          <span className="text-[14px] font-medium text-neutral-900">
            Use new provider
          </span>
        </div>
        <ChevronRight size={18} className="text-brand" />
      </button> */}

      {/* Search Input */}
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400"
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search providers"
          className="h-[48px] w-full rounded-[8px] border border-transparent bg-surface-input pl-10 pr-10 text-[13px] text-neutral-900 placeholder:text-neutral-400 focus:border-brand focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-ring transition-all"
        />
        {isLoading && (
          <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
            <Loader2 size={16} className="animate-spin text-neutral-400" />
          </div>
        )}
      </div>

      {/* Provider List Cards */}
      <div className="flex flex-col gap-2.5">
        {isLoading && effectiveProviders.length === 0 ? (
          <div className="flex items-center justify-center rounded-xl border border-neutral-200 bg-white p-8 text-neutral-500 gap-2">
            <Loader2 size={18} className="animate-spin text-brand" />
            <span className="text-[13px]">Loading payout providers...</span>
          </div>
        ) : effectiveProviders.length > 0 ? (
          effectiveProviders.map((prov) => {
            const isSelected = selectedProviderId === prov.id;
            return (
              <div
                key={prov.id}
                onClick={() => onSelectProvider(prov)}
                className={`flex cursor-pointer items-center justify-between rounded-xl border p-3.5 transition-all ${
                  isSelected
                    ? "border-brand bg-white shadow-xs ring-2 ring-brand-ring"
                    : "border-neutral-200 bg-white hover:border-neutral-300"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-badge text-[12px] font-semibold text-brand">
                    {prov.avatarText || "P"}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[14px] font-medium text-neutral-900">
                      {prov.name}
                    </span>
                    <span className="font-mono text-[12px] text-neutral-500">
                      {prov.accountNumber}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="hidden text-[12px] text-neutral-500 sm:inline">
                    {prov.companyName}
                  </span>
                  <ChevronRight size={16} className="text-neutral-400" />
                </div>
              </div>
            );
          })
        ) : (
          <div className="rounded-xl border border-dashed border-neutral-200 bg-neutral-50 p-6 text-center">
            <p className="text-[13px] font-medium text-neutral-700">
                  {isError
                    ? "Failed to load payout providers"
                    : "No saved payout providers found"}
            </p>
                {/* <p className="mt-1 text-[12px] text-neutral-500">
              Click &quot;Use new provider&quot; above to enter recipient account details.
            </p> */}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-4 flex items-center justify-end gap-3 border-t border-neutral-100 pt-4">
        <button
          type="button"
          onClick={onBack}
          className="h-10 rounded-[8px] px-4 py-2 text-[14px] font-medium text-neutral-700 transition-colors hover:text-neutral-900"
        >
          Cancel
        </button>
        <Button
          type="button"
          onClick={onNext}
          disabled={!selectedProviderId}
          className="px-6"
        >
          Next
        </Button>
      </div>
    </div>
  );
};
